import { ButtonTypeEnum, SnackBarVariants, USPSMIDTypeEnum } from '@app/app.enum';
import { history } from '@app/app.routes';
import withSnackBar from '@app/components/core/message.component';
import CustomButton from '@app/components/shared/custom-button.component';
import Icon from '@app/components/shared/icon.component';
import SecretKeySettingComponent from '@app/components/shared/secret-key-setting.component';
import { AddNewUSPSTierRoutedFromTierDropDown } from '@app/constants';
import * as models from '@app/models';
import { ISystemConfigs } from '@app/models';
import Injector from '@app/services';
import { AccountService } from '@app/services/account.service';
import { SharedService } from '@app/services/shared.service';
import { SystemConfigService } from '@app/services/system-config.service';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Grid,
	InputAdornment,
	ListItemText,
	MenuItem,
	OutlinedInput,
	Select,
	StyleRules,
	TextField,
	Typography,
	withStyles,
} from '@material-ui/core';
import Axios from 'axios';
import { Field, Form, Formik, FormikProps, FormikValues } from 'formik';
import * as React from 'react';
import { injectIntl } from 'react-intl';
import * as Yup from 'yup';
import USPSTiersConfigComponent from './usps-tiers.component';
import USPSeVSRefreshInductAccordion from './usps-evs-refresh-induct-accordion.component';

const accountService: AccountService = Injector.get(AccountService);
const sharedService: SharedService = Injector.get(SharedService);
const systemConfigService: SystemConfigService = Injector.get(SystemConfigService);

const styles: StyleRules = {};
const CreditCardFeeMin = 0;
const CreditCardFeeMax = 100;

class SettingsComponent extends React.Component<models.ISettingProps, models.ISettingState> {
	private CreditCardFeeSchema = Yup.object().shape<ISystemConfigs>({
		creditCardFee: Yup.number()
			.required(
				this.props.intl.formatMessage({
					id: 'settings.credit-card-fee-required',
				})
			)
			.min(
				CreditCardFeeMin,
				this.props.intl.formatMessage({
					id: 'settings.credit-card-fee-min',
				})
			)
			.max(
				CreditCardFeeMax,
				this.props.intl.formatMessage({
					id: 'settings.credit-card-fee-max',
				})
			),
	});

	constructor(props: models.ISettingProps) {
		super(props);
		this.state = {
			app: null,
			creditCardFee: 0,
			uspsMasterMID: '',
			creditCardFeeLoading: false,
			file: null,
			uspsMIDLoading: false,
			erroneousFile: null,
			showErrorMessage: false,
			fileName: null,
			uspsMIDType: USPSMIDTypeEnum.Outbound,
			USPSMIDsCount: {
				outBoundCount: 0,
				returnsCount: 0,
			},
		};
	}

	public componentDidMount() {
		const userData = accountService.getUserData();
		if (userData && userData.apps && userData.apps.length > 0) {
			// temporary we setting it first user app show.
			this.setState({
				app: userData.apps[0],
			});
		}
		this.getCreditCardFees();
		this.getUSPSMIDsCount();
	}

	public render() {
		const appDataToShow = this.state.app;
		return (
			<>
				<Grid container={true} direction='column' spacing={2}>
					{/* SecretKey Accordion */}
					<Grid item={true} lg={12} md={12} sm={12} xs={12}>
						<Accordion>
							<AccordionSummary expandIcon={<Icon name='chevron-down' />}>
								<Typography variant='h6' color='inherit'>
									{this.props.intl.formatMessage({
										id: 'app.api-key.api-key-secret',
									})}
								</Typography>
							</AccordionSummary>
							<AccordionDetails>
								{appDataToShow && (
									<SecretKeySettingComponent appDataToShow={appDataToShow} onApiKeyRegenrated={this.generateKey} />
								)}
							</AccordionDetails>
						</Accordion>
					</Grid>
					{/* System Settings Accordion */}
					<Grid item={true} lg={12} md={12} sm={12} xs={12}>
						<Accordion>
							<AccordionSummary expandIcon={<Icon name='chevron-down' />}>
								<Typography variant='h6' color='inherit'>
									{this.props.intl.formatMessage({
										id: 'settings.system-setting',
									})}
								</Typography>
							</AccordionSummary>
							<AccordionDetails>
								<Formik
									initialValues={{
										creditCardFee: this.state.creditCardFee,
									}}
									enableReinitialize={true}
									validationSchema={this.CreditCardFeeSchema}
									onSubmit={async (e: ISystemConfigs) => {
										try {
											this.setState({
												creditCardFeeLoading: true,
											});
											await systemConfigService.updateCreditCardFee(e);
											this.setState({
												creditCardFee: e.creditCardFee,
											});
											this.openSnackbar(
												this.props.intl.formatMessage({
													id: 'settings.credit-card-fee-snackbar',
												})
											);
										} catch (error) {
											if (!Axios.isCancel(error)) {
												this.openSnackbar(sharedService.parseError(error.data), SnackBarVariants.Error);
											}
										}
									}}
									render={(props: FormikProps<FormikValues>) => (
										<Form noValidate={true} className='width-100'>
											<Grid container={true} alignItems='center'>
												<Grid item={true} xs={12} md={2} lg={3}>
													<Typography variant='subtitle2' color='inherit'>
														{this.props.intl.formatMessage({
															id: 'settings.credit-card-fee-label',
														})}
													</Typography>
												</Grid>
												<Grid item={true} xs={12} md={10} lg={9}>
													<Field
														name='creditCardFee'
														margin='normal'
														type='number'
														component={TextField}
														required={true}
														fullWidth={true}
														value={props.values.creditCardFee}
														onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
															props.handleChange('creditCardFee')(e)
														}
														error={!!props.errors.creditCardFee}
														helperText={props.errors.creditCardFee}
														InputProps={{
															inputProps: {
																step: 0.1,
																min: CreditCardFeeMin,
																max: CreditCardFeeMax,
															},
															endAdornment: <InputAdornment position='end'>%</InputAdornment>,
														}}
													/>
												</Grid>
											</Grid>

											<Grid container={true} alignItems='center' justifyContent='center' className='mt-2 mb-2'>
												{/* Button component */}
												<CustomButton
													disabled={this.state.creditCardFeeLoading || !!Object.keys(props.errors).length}
													buttonText={this.props.intl.formatMessage({
														id: 'app.profile.user-info-form.save-button-text',
													})}
													loading={this.state.creditCardFeeLoading}
												/>
											</Grid>
										</Form>
									)}
								/>
							</AccordionDetails>
						</Accordion>
					</Grid>
					{/* USPS Tier Accordion */}
					<Grid item={true} lg={12} md={12} sm={12} xs={12}>
						<Accordion
							defaultExpanded={
								history.location.state?.extraParams?.isRoutedFrom === AddNewUSPSTierRoutedFromTierDropDown
							}
						>
							<AccordionSummary expandIcon={<Icon name='chevron-down' />}>
								<Typography variant='h6' color='inherit'>
									{this.props.intl.formatMessage({
										id: 'settings.usps-config-label',
									})}
								</Typography>
							</AccordionSummary>
							<AccordionDetails className='pt-0'>
								<USPSTiersConfigComponent uspsMasterMID={this.state.uspsMasterMID} openSnackBar={this.openSnackbar} />
							</AccordionDetails>
						</Accordion>
					</Grid>
					{/* USPSMID Accordion */}
					<Grid item={true} lg={12} md={12} sm={12} xs={12}>
						<Accordion>
							<AccordionSummary
								expandIcon={<Icon name='chevron-down' />}
								aria-controls='panel1a-content'
								id='panel1a-header'
							>
								<Typography variant='h6' color='inherit'>
									{this.props.intl.formatMessage({
										id: 'settings.uspsmid-accordion-label',
									})}
								</Typography>
							</AccordionSummary>
							<AccordionDetails>
								<Formik
									initialValues={null}
									enableReinitialize={true}
									validationSchema={null}
									onSubmit={async (e: any) => {
										try {
											this.setState({
												uspsMIDLoading: true,
											});

											const formData = new FormData();
											formData.append('file', this.state.file);
											const result = await systemConfigService.importUSPSMID(formData, this.state.uspsMIDType);
											if (result.statusCode === 422) {
												this.setState({
													erroneousFile: result.file,
													showErrorMessage: true,
													fileName: result.fileName,
												});
											} else {
												this.openSnackbar(
													this.props.intl.formatMessage({
														id: 'settings.uspsmid.import-uspsmid-success-snackbar',
													})
												);
												this.getUSPSMIDsCount();
												this.setState({
													uspsMIDLoading: false,
													file: null,
												});
											}
										} catch (error) {
											this.setState({
												uspsMIDLoading: false,
											});

											if (!Axios.isCancel(error)) {
												this.openSnackbar(sharedService.parseError(error.data), SnackBarVariants.Error);
											}
										}
									}}
									render={(props: FormikProps<FormikValues>) => (
										<Form noValidate={true} className='width-100'>
											<Grid container={true} alignItems='center'>
												<Grid item={true} xs={12} md={2} lg={3}>
													<Typography variant='subtitle2' color='inherit'>
														{this.props.intl.formatMessage({
															id: 'settings.uspsmid-type-label',
														})}
													</Typography>
												</Grid>

												<Grid item={true} xs={12} md={10} lg={9}>
													<Grid container={true} alignItems='center' spacing={2}>
														<Grid item={true} xs={6} md={9}>
															<Select
																fullWidth={true}
																input={<OutlinedInput labelWidth={0} />}
																value={this.state.uspsMIDType}
																onChange={(e: any) => this.applyUSPSMIDTypeFilters(e.target.value)}
																renderValue={(selected: any) => (selected as string[]).toString()}
															>
																{Object.keys(USPSMIDTypeEnum).map((value: USPSMIDTypeEnum, index: number) => (
																	<MenuItem key={index} value={value}>
																		<ListItemText
																			primary={this.props.intl.formatMessage({
																				id: `settings.usps-mid-type-label-${value}`,
																			})}
																		/>
																	</MenuItem>
																))}
															</Select>
														</Grid>
													</Grid>
												</Grid>
											</Grid>

											<Grid container={true} alignItems='center' className='mt-1'>
												<Grid item={true} xs={12} md={2} lg={3}>
													<Typography variant='subtitle2' color='inherit'>
														{this.props.intl.formatMessage({
															id: 'settings.uspsmid.available-mids-count-text',
														})}
													</Typography>
												</Grid>

												<Grid item={true} xs={12} md={10} lg={9} className='pt-2'>
													<Grid container={true} alignItems='center' spacing={2}>
														<Grid item={true} xs={6} md={9}>
															{this.getCountBasedOnMIDType()}
														</Grid>
													</Grid>
												</Grid>
											</Grid>

											<Grid container={true} alignItems='center' className='mt-2 mb-2'>
												<Grid item={true} xs={12} md={2} lg={3}>
													<Typography variant='subtitle2' color='inherit'>
														{this.props.intl.formatMessage({
															id: 'settings.uspsmid-file-label',
														})}
													</Typography>
												</Grid>
												<Grid item={true} xs={12} md={10} lg={9}>
													<Grid container={true} alignItems='center' spacing={2}>
														<Grid item={true} xs={6} md={9}>
															<TextField
																disabled={true}
																value={(this.state.file && this.state.file.name) || ''}
																fullWidth={true}
																placeholder={this.props.intl.formatMessage({
																	id: 'settings.uspsmid.import-default-text-textbox',
																})}
																helperText={
																	this.state.showErrorMessage === true
																		? this.props.intl.formatMessage({ id: 'settings.uspsmid.error-file-text' })
																		: null
																}
																error={this.state.showErrorMessage === true ? true : false}
															/>
														</Grid>
														<Grid item={true} xs={6} md={3}>
															<CustomButton
																ignoreFormikStateForDisable={true}
																disabled={false}
																onClick={this.showFileDialog}
																buttonText={this.props.intl.formatMessage({
																	id: 'settings.uspsmid.import-uspsmid-select-button',
																})}
															/>
														</Grid>
													</Grid>
												</Grid>
											</Grid>

											{!this.state.showErrorMessage ? (
												<Grid container={true} alignItems='center' justifyContent='center' className='mt-2 mb-2'>
													{/* Button component */}
													<CustomButton
														ignoreFormikStateForDisable={true}
														disabled={
															this.state.uspsMIDLoading ||
															!!Object.keys(props.errors).length ||
															!this.state.file ||
															!this.state.uspsMIDType
														}
														buttonText={this.props.intl.formatMessage({
															id: 'settings.uspsmid.import-button-text',
														})}
														loading={this.state.uspsMIDLoading}
													/>
												</Grid>
											) : null}
											{this.state.showErrorMessage ? (
												<Grid container={true} justifyContent='center' spacing={2}>
													<Grid item={true} xs={12} md={4} />

													<Grid item={true} xs={12} md={4}>
														<Grid container={true} spacing={2}>
															<Grid item={true}>
																{/* Button component */}
																<CustomButton
																	ignoreFormikStateForDisable={true}
																	buttonText={this.props.intl.formatMessage({
																		id: 'settings.uspsmid.download-button-text',
																	})}
																	onClick={() => this.exportErrorFile(this.state.erroneousFile)}
																	type={ButtonTypeEnum.Button}
																/>
															</Grid>

															<Grid item={true}>
																{/* Button component */}
																<CustomButton
																	ignoreFormikStateForDisable={true}
																	buttonText={this.props.intl.formatMessage({
																		id: 'settings.uspsmid.reset-button-text',
																	})}
																	onClick={() => this.resetToDefault()}
																	type={ButtonTypeEnum.Button}
																/>
															</Grid>
														</Grid>
													</Grid>
													<Grid item={true} xs={12} md={4} />
												</Grid>
											) : null}
										</Form>
									)}
								/>
							</AccordionDetails>
						</Accordion>
					</Grid>
					{/* USPS Induct Zip refresh accordion */}
					<Grid item={true} xs={12}>
						<USPSeVSRefreshInductAccordion openSnackBar={this.props.openSnackBar} />
					</Grid>
				</Grid>
			</>
		);
	}

	private getCountBasedOnMIDType() {
		switch (this.state.uspsMIDType) {
			case USPSMIDTypeEnum.Outbound:
				return this.state.USPSMIDsCount.outBoundCount;

			case USPSMIDTypeEnum.Returns:
				return this.state.USPSMIDsCount.returnsCount;
		}
	}

	private applyUSPSMIDTypeFilters = (midType: USPSMIDTypeEnum) => {
		this.setState({ uspsMIDType: midType });
	};

	private generateKey = (app: models.IAccountAppResponseVM) => {
		this.setState({
			app,
		});
	};

	private async getCreditCardFees() {
		try {
			const systemConfig = await systemConfigService.getCreditCardFeeAndMasterMID();
			this.setState({
				creditCardFee: systemConfig.creditCardFee,
				uspsMasterMID: systemConfig.uspsMasterMID,
			});
		} catch (error) {
			if (!Axios.isCancel(error)) {
				this.openSnackbar(sharedService.parseError(error.data), SnackBarVariants.Error);
			}
		}
	}

	private async getUSPSMIDsCount() {
		try {
			const USPSMIDsCountResult = await systemConfigService.getUSPSMIDsCount();
			this.setState({ USPSMIDsCount: USPSMIDsCountResult });
		} catch (error) {
			if (!Axios.isCancel(error)) {
				this.openSnackbar(sharedService.parseError(error.data), SnackBarVariants.Error);
			}
		}
	}

	// open  snackbar
	private openSnackbar = (message: string | string[], variantName: SnackBarVariants = SnackBarVariants.Success) => {
		this.setState({
			creditCardFeeLoading: false,
		});
		this.props.openSnackBar(message, variantName);
	};
	// open file dialog
	private showFileDialog = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		e.preventDefault();

		sharedService.showFileDialog(false, '.xlsx', async (files) => {
			this.setState({
				file: files[0],
			});
		});
	};

	// export  error file
	private exportErrorFile = (data: any) => {
		const binaryString = window.atob(data);
		const binaryLen = binaryString.length;
		const bytes = new Uint8Array(binaryLen);
		for (let i = 0; i < binaryLen; i++) {
			const ascii = binaryString.charCodeAt(i);
			bytes[i] = ascii;
		}
		const blob = new Blob([bytes], { type: 'application/octet-stream' });
		sharedService.downloadFile(blob, this.state.fileName);
	};

	// Reset USPSMID component to default
	private resetToDefault = () => {
		this.setState({
			showErrorMessage: false,
			file: null,
			uspsMIDLoading: false,
			uspsMIDType: USPSMIDTypeEnum.Outbound,
		});
	};
}

export default withSnackBar(withStyles(styles)(injectIntl(SettingsComponent)));



// WEBPACK FOOTER //
// ./src/components/admin/setting/settings.component.tsx